import React, { useState, useEffect, useRef } from 'react';

interface WagerControlProps {
    currentGold: number;
    wager: number;
    onChange: (val: number) => void;
}

export const WagerControl: React.FC<WagerControlProps> = ({ currentGold, wager, onChange }) => {
    const [internalVal, setInternalVal] = useState(wager);

    useEffect(() => {
        setInternalVal(wager);
    }, [wager]);

    const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const rawVal = parseInt(e.target.value);
        let newVal = rawVal;

        // Magnetic Logic: Snap to nearest 5 if within 1 unit distance
        // 5 -> 5
        // 6 -> 5
        // 7 -> 7
        // 8 -> 8
        // 9 -> 10
        // 10 -> 10
        const remainder = rawVal % 5;
        if (remainder === 1) newVal = rawVal - 1;       // Snap down (6->5)
        else if (remainder === 4) newVal = rawVal + 1;  // Snap up (9->10)

        // Ensure bounds
        if (newVal > currentGold) newVal = currentGold;

        setInternalVal(newVal);
        onChange(newVal);
    };

    // Allow freedom via direct input
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = parseInt(e.target.value);
        if (!isNaN(val)) {
            const clamped = Math.min(Math.max(0, val), currentGold);
            setInternalVal(clamped);
            onChange(clamped);
        } else if (e.target.value === '') {
            setInternalVal(0);
            onChange(0);
        }
    };

    const percentage = Math.min(100, Math.max(0, (internalVal / currentGold) * 100));

    return (
        <div className="w-full bg-zinc-900/50 border border-zinc-700/50 rounded-lg p-6 flex flex-col gap-4">
            <div className="flex justify-between items-end">
                <div>
                    <h4 className="text-sm font-bold text-tudor-gold uppercase tracking-wider flex items-center gap-2">
                        War Chest Wager
                        {/* <span className="text-[10px] text-zinc-500 font-normal normal-case border border-zinc-700 px-1.5 rounded">Magnetic Slider Active</span> */}
                    </h4>
                    <p className="text-xs text-zinc-500 mt-1">
                        Stake your gold. Winner takes all.
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <div className="text-right">
                        <div className="text-2xl font-mono text-white font-bold tracking-tight">
                            {internalVal}<span className="text-tudor-gold text-sm ml-1">g</span>
                        </div>
                        <div className="text-[10px] text-zinc-600 uppercase tracking-widest">
                            / {currentGold} Available
                        </div>
                    </div>
                </div>
            </div>

            <div className="relative h-6 flex items-center">
                {/* Custom Track Background */}
                <div className="absolute w-full h-2 bg-zinc-800 rounded-full overflow-hidden">
                    <div
                        className="h-full bg-gradient-to-r from-yellow-900 to-tudor-gold transition-all duration-75 ease-out"
                        style={{ width: `${percentage}%` }}
                    />
                </div>

                {/* The Slider Input */}
                <input
                    type="range"
                    min="0"
                    max={currentGold}
                    step="1"
                    value={internalVal}
                    onChange={handleSliderChange}
                    className="w-full absolute z-10 opacity-0 cursor-pointer h-full"
                />

                {/* Custom Thumb (Visual Only - follows percentage) */}
                <div
                    className="absolute w-5 h-5 bg-white border-2 border-tudor-gold rounded-full shadow-lg pointer-events-none transition-all duration-75 ease-out flex items-center justify-center z-0"
                    style={{ left: `calc(${percentage}% - 10px)` }}
                >
                    <div className={`w-1.5 h-1.5 rounded-full ${internalVal % 5 === 0 ? 'bg-tudor-gold' : 'bg-zinc-400'}`} />
                </div>
            </div>

            {/* Ticks/Labels for context */}
            <div className="flex justify-between text-[10px] text-zinc-600 font-mono uppercase">
                <span>0g</span>
                <span>{(currentGold / 2).toFixed(0)}g</span>
                <span>{currentGold}g</span>
            </div>
        </div>
    );
};
